import { Accordion } from '@liveauctioneers/hammer-ui-core/accordion';
import { Checkbox } from '@liveauctioneers/hammer-ui-core/checkbox';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { ViewMore } from '@liveauctioneers/hammer-ui-core/viewMore';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useNavigateSearchPageWithParams } from '@/pages/SearchPageSimplified/hooks/useNavigateSearchPageWithParams';
import { CurrentOpenMenu, MAX_FILTER_OPTIONS_COUNT } from '@/pages/SearchPageSimplified/types';
import { getRoundedNumberText } from '@/pages/SearchPageSimplified/utils/getRoundedNumberText';
import { useAppSelector } from '@/redux/hooks';
import { getMergedOptionFacet } from '@/redux/modules/searchFacetAggregates.selectors';
import { FacetOption } from '@/types/FacetOption';
import FacetTypes from '@/types/search/enums/facetTypes';
import { FilterAction } from '@/types/SearchFacet';
import { modifyParamsRemovePage, modifyParamsWhenChecked, parseQueryParams } from '@/utils/queryParams';

type Props = {
    isArchivedSearch: boolean;
};

export const StyleAndPeriodFilter = ({ isArchivedSearch }: Props) => {
    const { formatMessage } = useIntl();
    const [isExpanded, setIsExpanded] = useState(false);
    const { search } = useLocation();
    const queryParams = parseQueryParams(search);
    const navigateWithParams = useNavigateSearchPageWithParams();
    const styleFacet = useAppSelector((state) =>
        getMergedOptionFacet(state, isArchivedSearch, FacetTypes.STYLE_PERIOD_ID)
    );
    const { options = [] } = styleFacet;

    const onStyleOptionClick = (option: FacetOption) => {
        const newCheckStatus = !option.selected;

        let newParams = modifyParamsWhenChecked(queryParams, FacetTypes.STYLE_PERIOD_ID, option.id, newCheckStatus);

        newParams = modifyParamsRemovePage(newParams);

        navigateWithParams({
            currentOpenMenu: CurrentOpenMenu.AllFilters,
            filterAction: newCheckStatus ? FilterAction.StylePeriodChanged : FilterAction.StylePeriodRemoved,
            params: newParams,
            preventScrollReset: true,
        });
    };

    const filteredOptions = useMemo(() => {
        const sortedOptions = options
            .filter((option) => option.count !== 0 || option.selected)
            .sort((a, b) => b.count - a.count);
        if (!isExpanded) {
            return sortedOptions.slice(0, MAX_FILTER_OPTIONS_COUNT);
        }
        return sortedOptions;
    }, [isExpanded, options]);

    if (filteredOptions.length === 0) {
        return null;
    }

    return (
        <Accordion
            className="w-full"
            density="compact"
            emphasis="subtle"
            headline={formatMessage({ id: 'search_facet_title_stylePeriod' })}
            open
        >
            <ViewMore
                collapseText={formatMessage({ id: 'view_less' })}
                expandText={formatMessage({ id: 'view_more' })}
                fadeBackgroundColor="#fff"
                onChange={(isExpanded) => setIsExpanded(isExpanded)}
                textCutoffHeight={200}
            >
                <div
                    className={cn('flex max-h-[400px] flex-col gap-xs overflow-y-hidden', {
                        'overflow-y-auto': isExpanded,
                    })}
                >
                    {filteredOptions.map((option) => (
                        <Checkbox.Option
                            checked={option.selected}
                            disabled={option.count === 0}
                            key={option.id}
                            onChange={() => onStyleOptionClick(option)}
                        >
                            {option.name} ({getRoundedNumberText({ totalFound: option.count })})
                        </Checkbox.Option>
                    ))}
                </div>
            </ViewMore>
        </Accordion>
    );
};
